import React, { useMemo } from 'react';
import { Alert } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';
import ManagedInput from 'components/form/managed_input';
import ToggleOverrideSection from '../shared/toggle_override_section';

interface AvailabilityPickerProps {
  defaultAvailability?: string | null;
  selectedAvailability?: string | null;
  enableForSaleAndLease?: boolean;
  onUpdateAvailability: (payload: {
    availability?: string[],
    enableForSaleAndLease?: boolean
  }) => void;
}

export default function AvailabilityPicker({
  defaultAvailability,
  selectedAvailability = null,
  enableForSaleAndLease,
  onUpdateAvailability
}: AvailabilityPickerProps) {
  const getAvailabilityArray = (availability: string | null | undefined): string[] => {
    if (availability === 'sale_and_lease') {
      return ['sale', 'lease'];
    }
    if (availability === null || availability === undefined) {
      return [];
    }
    return [availability];
  };

  const currentAvailability = getAvailabilityArray(selectedAvailability);

  const handleUpdateAvailability = (availabilityType: string, value: boolean) => {
    const newAvailability = value
      ? [...currentAvailability, availabilityType]
      : currentAvailability.filter(type => type !== availabilityType);

    onUpdateAvailability({
      availability: newAvailability
    });
  };

  const handleSetDefaults = () => {
    if (defaultAvailability === 'sale_and_lease') {
      onUpdateAvailability({ enableForSaleAndLease: true });
    } else {
      const defaultArray = getAvailabilityArray(defaultAvailability);
      onUpdateAvailability({ enableForSaleAndLease: false, availability: defaultArray });
    }
  };

  const canSetDefault: boolean = useMemo(() => {
    if (defaultAvailability === undefined || defaultAvailability === null) return false;
    if (enableForSaleAndLease && defaultAvailability === 'sale_and_lease') return false;
    if (enableForSaleAndLease && defaultAvailability !== 'sale_and_lease') return true;
    return defaultAvailability !== selectedAvailability;
  }, [defaultAvailability, selectedAvailability, enableForSaleAndLease]);

  const renderDefaultAlert = () => {
    if (!canSetDefault) return null;

    return (
      <Alert className="text-center" variant="info">
        <div>
          The availability has been edited from the initial configuration.
          Selecting "revert" below will undo those changes,
          and restore the availability selected when this template was created.
        </div>
        <div className="btn btn-ghost btn-sm mt-1" onClick={handleSetDefaults}>
          <FontAwesomeIcon icon={faUndo} />
          Revert Availability
        </div>
      </Alert>
    );
  };

  return (
    <React.Fragment>
      <ToggleOverrideSection
        checked={enableForSaleAndLease}
        name="availability-override"
        subtitle="Unchecking will allow you to make this document template available for sale or lease"
        title="Enable for sale and lease"
        onOverride={handleSetDefaults}
        onRemove={() => onUpdateAvailability({ enableForSaleAndLease: true })}
      />
      {renderDefaultAlert()}
      {!enableForSaleAndLease && (
        <div className="d-flex flex-column gap-2">
          <ManagedInput
            checked={currentAvailability.includes('sale')}
            label="For Sale"
            name="availability-sale"
            type="checkbox"
            onValueChange={(value: boolean) => handleUpdateAvailability('sale', value)}
          >
            For Sale
          </ManagedInput>
          <ManagedInput
            checked={currentAvailability.includes('lease')}
            label="For Lease"
            name="availability-lease"
            type="checkbox"
            onValueChange={(value: boolean) => handleUpdateAvailability('lease', value)}
          >
            For Lease
          </ManagedInput>
        </div>
      )}
    </React.Fragment>
  );
}
